/**
 * Gerenciador de posições para FloatingPhotos - Versão 2.0
 * Sistema robusto baseado em grid virtual e forças determinísticas
 */

export interface Position {
  x: number;
  y: number;
}

export interface PhotoInstance {
  id: string;
  position: Position;
  targetPosition: Position; // Posição alvo para interpolação suave
  size: number;
  velocity: { x: number; y: number };
  isNew: boolean;
  isExiting: boolean;
  // Campos para estabilização
  zone: number; // Zona do grid virtual
  lastMoveTime: number;
  isStable: boolean;
  correctionCount: number; // Contador de correções consecutivas
}

export interface CenterTextBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

export interface ExclusionZone {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface GridZone {
  id: number;
  bounds: { left: number; right: number; top: number; bottom: number };
  occupied: boolean;
  occupantId?: string;
}

export class PositionManagerV2 {
  private photoInstances: Map<string, PhotoInstance> = new Map();
  private centerTextBounds?: CenterTextBounds;
  private topLeftExclusionZone?: ExclusionZone;
  private screenWidth: number = window.innerWidth;
  private screenHeight: number = window.innerHeight;
  private virtualGrid: GridZone[] = [];
  private gridCols = 10; // 10x8 grid virtual para mais zonas
  private gridRows = 8;
  
  // Configurações otimizadas
  private readonly minDistance = 80; // Distância mínima entre fotos
  private readonly centerTextMargin = 80; // Margem para centerText
  private readonly exclusionZoneMargin = 20; // Margem para zona de exclusão
  private readonly nameHeight = 60; // Altura considerada para o nome
  private readonly stabilityThreshold = 2; // Máximo de correções antes de estabilizar
  
  constructor(centerTextBounds?: CenterTextBounds, topLeftExclusionZone?: ExclusionZone) {
    this.centerTextBounds = centerTextBounds;
    this.topLeftExclusionZone = topLeftExclusionZone;
    this.updateScreenSize();
    this.initializeVirtualGrid();
    
    window.addEventListener('resize', () => {
      this.updateScreenSize();
      this.initializeVirtualGrid();
    });
  }

  private updateScreenSize() {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
  }

  // Inicializar grid virtual para distribuição espacial
  private initializeVirtualGrid() {
    this.virtualGrid = [];
    const cellWidth = this.screenWidth / this.gridCols;
    const cellHeight = this.screenHeight / this.gridRows;
    
    let zoneId = 0;
    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const left = col * cellWidth;
        const right = (col + 1) * cellWidth;
        const top = row * cellHeight;
        const bottom = (row + 1) * cellHeight;
        
        // Verificar se a zona conflita com centerText ou zona de exclusão
        const conflictsWithCenter = this.centerTextBounds && 
          this.zoneConflictsWithCenterText({ left, right, top, bottom });
        
        const conflictsWithExclusion = this.topLeftExclusionZone &&
          this.zoneConflictsWithExclusionZone({ left, right, top, bottom });
        
        if (!conflictsWithCenter && !conflictsWithExclusion) {
          this.virtualGrid.push({
            id: zoneId++,
            bounds: { left, right, top, bottom },
            occupied: false
          });
        }
      }
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔧 [PositionManagerV2] Grid virtual criado: ${this.virtualGrid.length} zonas disponíveis`);
      if (this.topLeftExclusionZone) {
        console.log(`🚫 [PositionManagerV2] Zona de exclusão superior esquerda: ${this.topLeftExclusionZone.width}x${this.topLeftExclusionZone.height}px`);
      }
    }
  }

  private zoneConflictsWithExclusionZone(zoneBounds: { left: number; right: number; top: number; bottom: number }): boolean {
    if (!this.topLeftExclusionZone) return false;
    
    const margin = this.exclusionZoneMargin;
    const exclLeft = this.topLeftExclusionZone.left - margin;
    const exclRight = this.topLeftExclusionZone.left + this.topLeftExclusionZone.width + margin;
    const exclTop = this.topLeftExclusionZone.top - margin;
    const exclBottom = this.topLeftExclusionZone.top + this.topLeftExclusionZone.height + margin;
    
    return !(zoneBounds.right < exclLeft || 
             zoneBounds.left > exclRight || 
             zoneBounds.bottom < exclTop || 
             zoneBounds.top > exclBottom);
  }

  private zoneConflictsWithCenterText(zoneBounds: { left: number; right: number; top: number; bottom: number }): boolean {
    if (!this.centerTextBounds) return false;
    
    const margin = this.centerTextMargin;
    const textLeft = this.centerTextBounds.left - margin;
    const textRight = this.centerTextBounds.right + margin;
    const textTop = this.centerTextBounds.top - margin;
    const textBottom = this.centerTextBounds.bottom + margin;
    
    return !(zoneBounds.right < textLeft || 
             zoneBounds.left > textRight || 
             zoneBounds.bottom < textTop || 
             zoneBounds.top > textBottom);
  }

  // Encontrar zona livre mais adequada (melhor distribuição)
  private findBestDistributedZone(isHighlight: boolean = false): GridZone | null {
    const availableZones = this.virtualGrid.filter(zone => !zone.occupied);
    
    if (availableZones.length === 0) return null;
    
    // Obter todas as posições de fotos existentes
    const existingPositions = Array.from(this.photoInstances.values())
      .filter(p => !p.isExiting)
      .map(p => ({
        x: p.position.x + p.size / 2,
        y: p.position.y + p.size / 2
      }));
    
    // Calcular pontuação para cada zona baseada na distância das fotos existentes
    const zonesWithScores = availableZones.map(zone => {
      const zoneCenterX = (zone.bounds.left + zone.bounds.right) / 2;
      const zoneCenterY = (zone.bounds.top + zone.bounds.bottom) / 2;
      
      // Calcular distância mínima para qualquer foto existente
      let minDistanceToPhoto = Infinity;
      let totalDistanceToPhotos = 0;
      
      for (const pos of existingPositions) {
        const distance = Math.sqrt(
          Math.pow(zoneCenterX - pos.x, 2) + 
          Math.pow(zoneCenterY - pos.y, 2)
        );
        minDistanceToPhoto = Math.min(minDistanceToPhoto, distance);
        totalDistanceToPhotos += distance;
      }
      
      // Se não há fotos, usar distância do centro da tela para melhor distribuição inicial
      if (existingPositions.length === 0) {
        // Distribuir a partir das bordas para evitar aglomeração no centro
        const distFromCenter = Math.sqrt(
          Math.pow(zoneCenterX - this.screenWidth / 2, 2) + 
          Math.pow(zoneCenterY - this.screenHeight / 2, 2)
        );
        // Para destaques, preferir zonas mais próximas do centro (mas não sobre o texto)
        minDistanceToPhoto = isHighlight ? -distFromCenter * 0.5 : distFromCenter;
      }
      
      // Pontuação: quanto maior a distância mínima, melhor
      // Também considerar a média das distâncias para distribuição geral
      const avgDistance = existingPositions.length > 0 
        ? totalDistanceToPhotos / existingPositions.length 
        : 0;
      
      return {
        zone,
        score: minDistanceToPhoto * 2 + avgDistance * 0.5
      };
    });
    
    // Ordenar por pontuação (maior = melhor distribuição)
    zonesWithScores.sort((a, b) => b.score - a.score);
    
    // Adicionar aleatoriedade entre as melhores opções para evitar padrões
    const topCount = Math.min(3, zonesWithScores.length);
    const topZones = zonesWithScores.slice(0, topCount);
    const selectedIndex = Math.floor(Math.random() * topZones.length);
    
    return topZones[selectedIndex].zone;
  }

  // Encontrar zona livre mais próxima de uma posição (mantido para compatibilidade)
  private findNearestFreeZone(preferredPosition: Position, excludeZone?: number): GridZone | null {
    const availableZones = this.virtualGrid.filter(zone => 
      !zone.occupied && zone.id !== excludeZone
    );
    
    if (availableZones.length === 0) return null;
    
    // Ordenar por distância do centro da zona à posição preferida
    availableZones.sort((a, b) => {
      const aCenterX = (a.bounds.left + a.bounds.right) / 2;
      const aCenterY = (a.bounds.top + a.bounds.bottom) / 2;
      const bCenterX = (b.bounds.left + b.bounds.right) / 2;
      const bCenterY = (b.bounds.top + b.bounds.bottom) / 2;
      
      const aDistance = Math.sqrt(
        Math.pow(aCenterX - preferredPosition.x, 2) + 
        Math.pow(aCenterY - preferredPosition.y, 2)
      );
      const bDistance = Math.sqrt(
        Math.pow(bCenterX - preferredPosition.x, 2) + 
        Math.pow(bCenterY - preferredPosition.y, 2)
      );
      
      return aDistance - bDistance;
    });
    
    return availableZones[0];
  }

  // Gerar posição segura dentro de uma zona
  private generateSafePositionInZone(zone: GridZone, photoSize: number): Position {
    const margin = 30;
    const availableWidth = (zone.bounds.right - zone.bounds.left) - photoSize - (2 * margin);
    const availableHeight = (zone.bounds.bottom - zone.bounds.top) - photoSize - this.nameHeight - (2 * margin);
    
    const x = zone.bounds.left + margin + Math.random() * Math.max(0, availableWidth);
    const y = zone.bounds.top + margin + Math.random() * Math.max(0, availableHeight);
    
    return { x, y };
  }

  // Gerar posição segura dentro de uma zona com verificação de colisão
  private generateSafePositionInZoneWithCollisionCheck(zone: GridZone, photoSize: number, excludeId: string): Position {
    const margin = 30;
    const availableWidth = (zone.bounds.right - zone.bounds.left) - photoSize - (2 * margin);
    const availableHeight = (zone.bounds.bottom - zone.bounds.top) - photoSize - this.nameHeight - (2 * margin);
    
    // Tentar encontrar uma posição sem colisão
    const maxAttempts = 10;
    let bestPosition: Position | null = null;
    let bestMinDistance = 0;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const x = zone.bounds.left + margin + Math.random() * Math.max(0, availableWidth);
      const y = zone.bounds.top + margin + Math.random() * Math.max(0, availableHeight);
      const testPosition = { x, y };
      
      // Calcular distância mínima para outras fotos
      let minDistance = Infinity;
      const existingPhotos = Array.from(this.photoInstances.values()).filter(p => p.id !== excludeId && !p.isExiting);
      
      for (const photo of existingPhotos) {
        const distance = Math.sqrt(
          Math.pow(x + photoSize / 2 - (photo.position.x + photo.size / 2), 2) +
          Math.pow(y + photoSize / 2 - (photo.position.y + photo.size / 2), 2)
        );
        minDistance = Math.min(minDistance, distance);
      }
      
      // Se não há outras fotos, retornar primeira posição
      if (existingPhotos.length === 0) {
        return testPosition;
      }
      
      // Verificar se está longe o suficiente (sem colisão)
      const requiredDistance = (photoSize + this.minDistance);
      if (minDistance >= requiredDistance) {
        return testPosition;
      }
      
      // Guardar a melhor posição encontrada até agora
      if (minDistance > bestMinDistance) {
        bestMinDistance = minDistance;
        bestPosition = testPosition;
      }
    }
    
    // Retornar a melhor posição encontrada (mesmo que não seja ideal)
    return bestPosition || { 
      x: zone.bounds.left + margin, 
      y: zone.bounds.top + margin 
    };
  }

  // Gerar posição fallback quando não há zonas livres
  private generateFallbackPosition(photoSize: number): Position {
    const margin = 50;
    const x = margin + Math.random() * (this.screenWidth - photoSize - 2 * margin);
    const y = margin + Math.random() * (this.screenHeight - photoSize - this.nameHeight - 2 * margin);
    return { x, y };
  }

  // Atualizar bounds do centerText
  updateCenterTextBounds(bounds: CenterTextBounds) {
    this.centerTextBounds = bounds;
    this.initializeVirtualGrid(); // Recalcular grid quando centerText muda
  }

  // Registrar uma nova instância de foto
  registerPhoto(id: string, size: number, isNew: boolean = false, preferUpperHalf?: boolean, preferCenterThird?: boolean): Position {
    // Usar o novo algoritmo de distribuição otimizada
    const zone = this.findBestDistributedZone(isNew);
    
    if (!zone) {
      // Fallback: posição aleatória se não há zonas livres
      const position = this.generateFallbackPosition(size);
      this.photoInstances.set(id, {
        id,
        position,
        targetPosition: position,
        size,
        velocity: { x: 0, y: 0 },
        isNew,
        isExiting: false,
        zone: -1,
        lastMoveTime: Date.now(),
        isStable: false,
        correctionCount: 0
      });
      return position;
    }
    
    // Gerar posição segura dentro da zona, verificando contra fotos existentes
    const position = this.generateSafePositionInZoneWithCollisionCheck(zone, size, id);
    zone.occupied = true;
    zone.occupantId = id;
    
    this.photoInstances.set(id, {
      id,
      position,
      targetPosition: position,
      size,
      velocity: { x: 0, y: 0 },
      isNew,
      isExiting: false,
      zone: zone.id,
      lastMoveTime: Date.now(),
      isStable: true, // Começar estável em zona dedicada
      correctionCount: 0
    });

    if (process.env.NODE_ENV === 'development') {
      console.log(`📷 [PositionManagerV2] Foto registrada: ${id} na zona ${zone.id} - Posição: (${position.x.toFixed(1)}, ${position.y.toFixed(1)}) - Destaque: ${isNew}`);
    }

    return position;
  }

  // Atualizar posição de uma foto existente
  updatePhotoPosition(id: string, newPosition: Position, velocity: { x: number; y: number }): { position: Position; velocityCorrection?: { x: number; y: number } } {
    const instance = this.photoInstances.get(id);
    if (!instance) {
      return { position: newPosition };
    }
    
    const currentTime = Date.now();
    
    // Verificar se a foto deve ser estabilizada (muitas correções consecutivas)
    if (instance.correctionCount >= this.stabilityThreshold) {
      instance.isStable = true;
      instance.correctionCount = 0;
      
      // Retornar suavemente para a posição alvo
      const lerpFactor = 0.03;
      const targetPos = {
        x: instance.position.x + (instance.targetPosition.x - instance.position.x) * lerpFactor,
        y: instance.position.y + (instance.targetPosition.y - instance.position.y) * lerpFactor
      };
      
      instance.position = targetPos;
      this.photoInstances.set(id, instance);
      
      return { position: targetPos };
    }
    
    // Verificar colisões usando método mais simples e efetivo
    const hasCollision = this.hasSimpleCollision(newPosition, instance.size, id);
    
    if (hasCollision) {
      instance.correctionCount++;
      instance.isStable = false;
      instance.lastMoveTime = currentTime;
      
      // Buscar nova zona se necessário
      const correctedPosition = this.findSafeAlternativePosition(newPosition, instance.size, id);
      
      // Atualizar posição alvo para convergência suave
      instance.targetPosition = correctedPosition;
      
      // Calcular velocidade de correção suave
      const dx = correctedPosition.x - newPosition.x;
      const dy = correctedPosition.y - newPosition.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 0) {
        const correctionStrength = Math.min(30, distance * 0.3);
        const normalizedDx = dx / distance;
        const normalizedDy = dy / distance;
        
        const velocityCorrection = {
          x: normalizedDx * correctionStrength,
          y: normalizedDy * correctionStrength
        };
        
        instance.velocity = velocityCorrection;
        instance.position = newPosition; // Manter posição atual, target será aplicado gradualmente
        this.photoInstances.set(id, instance);
        
        return { 
          position: newPosition, 
          velocityCorrection 
        };
      }
    }
    
    // Posição válida, atualizar normalmente
    instance.position = newPosition;
    instance.targetPosition = newPosition;
    instance.velocity = velocity;
    instance.lastMoveTime = currentTime;
    instance.correctionCount = Math.max(0, instance.correctionCount - 1); // Reduzir contador gradualmente
    this.photoInstances.set(id, instance);
    
    return { position: newPosition };
  }

  // Verificação de colisão simplificada e mais robusta
  private hasSimpleCollision(position: Position, photoSize: number, excludeId: string): boolean {
    // Verificar colisão com centerText
    if (this.isCollidingWithCenterText(position, photoSize)) {
      return true;
    }
    
    // Verificar colisão com zona de exclusão superior esquerda
    if (this.isCollidingWithExclusionZone(position, photoSize)) {
      return true;
    }
    
    // Verificar colisão com bordas
    const totalHeight = photoSize + this.nameHeight;
    if (position.x < 0 || position.y < 0 || 
        position.x + photoSize > this.screenWidth || 
        position.y + totalHeight > this.screenHeight) {
      return true;
    }
    
    // Verificar colisão com outras fotos
    const instances = Array.from(this.photoInstances.entries());
    for (const [id, instance] of instances) {
      if (id === excludeId || instance.isExiting) continue;
      
      const distance = Math.sqrt(
        Math.pow(position.x - instance.position.x, 2) + 
        Math.pow(position.y - instance.position.y, 2)
      );
      
      const requiredDistance = (photoSize + instance.size) / 2 + this.minDistance;
      
      if (distance < requiredDistance) {
        return true;
      }
    }
    
    return false;
  }

  // Encontrar posição alternativa segura
  private findSafeAlternativePosition(currentPosition: Position, photoSize: number, excludeId: string): Position {
    // Primeiro tentar encontrar uma nova zona
    const instance = this.photoInstances.get(excludeId);
    const currentZone = instance ? instance.zone : -1;
    
    const newZone = this.findNearestFreeZone(currentPosition, currentZone);
    if (newZone) {
      // Liberar zona atual se ocupada
      if (currentZone >= 0) {
        const oldZone = this.virtualGrid.find(z => z.id === currentZone);
        if (oldZone) {
          oldZone.occupied = false;
          oldZone.occupantId = undefined;
        }
      }
      
      // Ocupar nova zona
      newZone.occupied = true;
      newZone.occupantId = excludeId;
      
      if (instance) {
        instance.zone = newZone.id;
        this.photoInstances.set(excludeId, instance);
      }
      
      return this.generateSafePositionInZone(newZone, photoSize);
    }
    
    // Fallback: tentar posições em círculo ao redor da posição atual
    const attempts = 12;
    const radius = this.minDistance * 1.5;
    
    for (let i = 0; i < attempts; i++) {
      const angle = (i / attempts) * Math.PI * 2;
      const testPosition = {
        x: currentPosition.x + Math.cos(angle) * radius,
        y: currentPosition.y + Math.sin(angle) * radius
      };
      
      if (!this.hasSimpleCollision(testPosition, photoSize, excludeId)) {
        return testPosition;
      }
    }
    
    // Último recurso: posição aleatória
    return this.generateFallbackPosition(photoSize);
  }

  // Verificar se uma posição colide com o centerText
  private isCollidingWithCenterText(position: Position, photoSize: number): boolean {
    if (!this.centerTextBounds) return false;
    
    const totalHeight = photoSize + this.nameHeight;
    const margin = this.centerTextMargin;
    
    const photoLeft = position.x;
    const photoRight = position.x + photoSize;
    const photoTop = position.y;
    const photoBottom = position.y + totalHeight;
    
    const textLeft = this.centerTextBounds.left - margin;
    const textRight = this.centerTextBounds.right + margin;
    const textTop = this.centerTextBounds.top - margin;
    const textBottom = this.centerTextBounds.bottom + margin;
    
    const overlapsHorizontally = photoRight > textLeft && photoLeft < textRight;
    const overlapsVertically = photoBottom > textTop && photoTop < textBottom;
    
    return overlapsHorizontally && overlapsVertically;
  }

  // Verificar se uma posição colide com a zona de exclusão superior esquerda
  private isCollidingWithExclusionZone(position: Position, photoSize: number): boolean {
    if (!this.topLeftExclusionZone) return false;
    
    const totalHeight = photoSize + this.nameHeight;
    const margin = this.exclusionZoneMargin;
    
    const photoLeft = position.x;
    const photoRight = position.x + photoSize;
    const photoTop = position.y;
    const photoBottom = position.y + totalHeight;
    
    const exclLeft = this.topLeftExclusionZone.left - margin;
    const exclRight = this.topLeftExclusionZone.left + this.topLeftExclusionZone.width + margin;
    const exclTop = this.topLeftExclusionZone.top - margin;
    const exclBottom = this.topLeftExclusionZone.top + this.topLeftExclusionZone.height + margin;
    
    const overlapsHorizontally = photoRight > exclLeft && photoLeft < exclRight;
    const overlapsVertically = photoBottom > exclTop && photoTop < exclBottom;
    
    return overlapsHorizontally && overlapsVertically;
  }

  // Atualizar zona de exclusão
  updateTopLeftExclusionZone(zone: ExclusionZone) {
    this.topLeftExclusionZone = zone;
    this.initializeVirtualGrid(); // Recalcular grid
  }

  // Obter zona de exclusão atual
  getTopLeftExclusionZone(): ExclusionZone | undefined {
    return this.topLeftExclusionZone;
  }

  // Atualizar tamanho de uma foto
  updatePhotoSize(id: string, newSize: number) {
    const instance = this.photoInstances.get(id);
    if (instance) {
      instance.size = newSize;
      this.photoInstances.set(id, instance);
    }
  }

  // Marcar foto como saindo
  markPhotoAsExiting(id: string) {
    const instance = this.photoInstances.get(id);
    if (instance) {
      instance.isExiting = true;
      
      // Liberar zona se ocupada
      if (instance.zone >= 0) {
        const zone = this.virtualGrid.find(z => z.id === instance.zone);
        if (zone) {
          zone.occupied = false;
          zone.occupantId = undefined;
        }
      }
      
      this.photoInstances.set(id, instance);
    }
  }

  // Remover foto do gerenciador
  removePhoto(id: string) {
    const instance = this.photoInstances.get(id);
    if (instance && instance.zone >= 0) {
      // Liberar zona
      const zone = this.virtualGrid.find(z => z.id === instance.zone);
      if (zone) {
        zone.occupied = false;
        zone.occupantId = undefined;
      }
    }
    
    this.photoInstances.delete(id);
  }

  // Obter posição atual de uma foto
  getPhotoPosition(id: string): Position | undefined {
    return this.photoInstances.get(id)?.position;
  }

  // Debug: obter informacoes do grid
  getGridInfo() {
    return {
      totalZones: this.virtualGrid.length,
      occupiedZones: this.virtualGrid.filter(z => z.occupied).length,
      freeZones: this.virtualGrid.filter(z => !z.occupied).length,
      photos: this.photoInstances.size
    };
  }
}
